import { API_URL } from '../services/api';

// Prefer explicit asset base when provided (e.g., http://127.0.0.1:8080)
const ASSET_BASE = process.env.REACT_APP_ASSET_BASE_URL;
const API_BASE = process.env.REACT_APP_API_BASE_URL;

// Compute backend origin from API_URL (works with absolute or relative '/api/v1').
export function getBackendOrigin(): string {
  try {
    const winOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const winPort = typeof window !== 'undefined' ? window.location.port : '';
    // Derive from API base or API_URL (works with absolute or relative '/api/v1')
    const src = (API_BASE && API_BASE.trim() !== '') ? API_BASE : API_URL;
    const base = new URL(src, winOrigin);
    if ((/^(localhost|127\.0\.0\.1)$/i.test(base.hostname)) && winPort === '3000') {
      return `${base.protocol}//${base.hostname}:8080`;
    }
    return base.origin;
  } catch {
    try {
      if (ASSET_BASE && ASSET_BASE.trim() !== '') {
        const u = new URL(ASSET_BASE, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
        return u.origin;
      }
    } catch {}
    return typeof window !== 'undefined' ? window.location.origin : '';
  }
}

// Utility to resolve asset URLs robustly.
// - For '/uploads' and '/dist' paths, always prefix with backend origin
// - For absolute URLs pointing to localhost/127.0.0.1, rewrite origin to backend origin if the path is '/uploads' or '/dist'
// - For data: URIs, return as-is
export function assetUrl(pathOrUrl?: string | null): string | undefined {
  if (!pathOrUrl) return undefined;
  try {
    const val = String(pathOrUrl);
    const backendOrigin = getBackendOrigin();
    // data URI
    if (/^data:/i.test(val)) return val;

    // Absolute URL
    if (/^(?:https?:)?\/\//i.test(val)) {
      const u = new URL(val, window.location.origin);
      const isLocalHost = /^(localhost|127\.0\.0\.1)(:\d+)?$/i.test(u.host);
      const isBackendAsset = u.pathname.startsWith('/uploads') || u.pathname.startsWith('/dist');
      const isSameAsFrontend = typeof window !== 'undefined' ? (u.origin === window.location.origin) : false;
      if ((isLocalHost || isSameAsFrontend) && isBackendAsset) {
        return new URL(u.pathname + u.search + u.hash, backendOrigin + '/').toString();
      }
      return u.toString();
    }

    // Relative URL
    if (val.startsWith('/uploads') || val.startsWith('/dist')) {
      return new URL(val, backendOrigin + '/').toString();
    }
    // Otherwise leave as-is (component may resolve relative to current origin)
    return val;
  } catch {
    return pathOrUrl || undefined;
  }
}

export function isUploadPath(pathOrUrl?: string | null): boolean {
  return !!pathOrUrl && /^\/uploads\//.test(pathOrUrl);
}

/**
 * Sanitizes club names by removing common suffixes and abbreviating long formal names
 * Examples:
 *  - "Tělovýchovná jednota Valašské Meziříčí, spolek" -> "TJ Valašské Meziříčí"
 *  - "MFK Slavoj Bruntál, z. s." -> "MFK Slavoj Bruntál"
 */
export function sanitizeClubName(name?: string | null): string {
  if (!name) return '';
  
  let sanitized = name.trim();
  
  // Replace full form "Tělovýchovná jednota" with "TJ"
  sanitized = sanitized.replace(/^Tělovýchovná jednota\s+/i, 'TJ ');
  
  // Replace full form "Sportovní klub" with "SK"
  sanitized = sanitized.replace(/^Sportovní klub\s+/i, 'SK ');
  
  // Replace full form "Fotbalový klub" with "FK"
  sanitized = sanitized.replace(/^Fotbalový klub\s+/i, 'FK ');
  
  // Replace full form "Fotbal klub" with "FK"
  sanitized = sanitized.replace(/^Fotbal klub\s+/i, 'FK ');
  
  // Replace full form "Sokol" abbreviation
  sanitized = sanitized.replace(/^Sokol\s+/i, 'SK ');
  
  // Remove common legal suffixes at the end
  sanitized = sanitized.replace(/,\s*(spolek|z\.\s*s\.|o\.\s*s\.|z\s*s|o\s*s|spolková\s+organizace|spolek\s+registrovaný|z\.s\.|o\.s\.)$/i, '');
  
  return sanitized.trim();
}
